import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Users, DollarSign, TrendingUp, Calendar, UserCheck, UserX, Building2, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/hiring`;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const KPIDashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      const employeeData = response.data;
      setEmployees(employeeData);
      calculateKPIs(employeeData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const calculateKPIs = (employeeData) => {
    const now = new Date();
    const activeEmployees = employeeData.filter(e => !e.end_date || new Date(e.end_date) >= now);
    
    // Calculate average tenure
    const tenures = activeEmployees.map(e => {
      const startDate = new Date(e.start_date);
      const years = (now - startDate) / (1000 * 60 * 60 * 24 * 365);
      return years;
    });
    const avgTenure = tenures.length > 0 ? tenures.reduce((a, b) => a + b, 0) / tenures.length : 0;

    // Calculate average age from date_of_birth
    const ages = activeEmployees
      .filter(e => e.date_of_birth)
      .map(e => {
        const birthDate = new Date(e.date_of_birth);
        const age = (now - birthDate) / (1000 * 60 * 60 * 24 * 365);
        return age;
      });
    const avgAge = ages.length > 0 ? ages.reduce((a, b) => a + b, 0) / ages.length : 25;

    // Gender distribution from actual data
    const maleCount = activeEmployees.filter(e => e.gender === 'Male').length;
    const femaleCount = activeEmployees.filter(e => e.gender === 'Female').length;

    // Employee type distribution
    const employeeTypeDistribution = activeEmployees.reduce((acc, emp) => {
      acc[emp.employee_type] = (acc[emp.employee_type] || 0) + 1;
      return acc;
    }, {});

    // Department distribution
    const departmentDistribution = activeEmployees.reduce((acc, emp) => {
      acc[emp.team] = (acc[emp.team] || 0) + 1;
      return acc;
    }, {});

    // Civil status distribution from actual data
    const civilStatus = activeEmployees.reduce((acc, emp) => {
      const status = emp.civil_status || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Age distribution from actual data
    const ageDistribution = ages.reduce((acc, age) => {
      if (age < 30) acc['20-29']++;
      else if (age < 40) acc['30-39']++;
      else if (age < 50) acc['40-49']++;
      else acc['50+']++;
      return acc;
    }, {
      '20-29': 0,
      '30-39': 0,
      '40-49': 0,
      '50+': 0,
    });

    // Tenure distribution
    const tenureDistribution = tenures.reduce((acc, tenure) => {
      if (tenure < 0.5) acc['6-12 Months']++;
      else if (tenure < 1) acc['12-18 Months']++;
      else if (tenure < 1.5) acc['18-24 Months']++;
      else if (tenure < 2) acc['2 Years']++;
      else if (tenure < 3) acc['3 Years']++;
      else if (tenure < 4) acc['4 Years']++;
      else acc['5 Years']++;
      return acc;
    }, {
      '6-12 Months': 0,
      '12-18 Months': 0,
      '18-24 Months': 0,
      '2 Years': 0,
      '3 Years': 0,
      '4 Years': 0,
      '5 Years': 0,
    });

    // Employment status from actual data
    const employmentStatusDistribution = activeEmployees.reduce((acc, emp) => {
      const status = emp.employment_status || 'Permanent';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    
    // Calculate permanent and contractor counts
    const permanentCount = activeEmployees.filter(e => e.employment_status === 'Permanent' || e.employee_type === 'Full-time').length;
    const contractorCount = activeEmployees.filter(e => e.employee_type === 'Contractor').length;

    setKpis({
      totalEmployees: employeeData.length,
      activeEmployees: activeEmployees.length,
      avgTenure: avgTenure.toFixed(1),
      avgAge: Math.round(avgAge),
      malePercentage: activeEmployees.length > 0 ? Math.round((maleCount / activeEmployees.length) * 100) : 0,
      femalePercentage: activeEmployees.length > 0 ? Math.round((femaleCount / activeEmployees.length) * 100) : 0,
      maleCount,
      femaleCount,
      employeeTypeDistribution,
      departmentDistribution,
      civilStatus,
      ageDistribution,
      tenureDistribution,
      employmentStatusDistribution,
      permanentCount,
      contractorCount,
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading KPI Dashboard...</div>;
  }

  const employeeTypeData = Object.entries(kpis.employeeTypeDistribution || {}).map(([name, value]) => ({ name, value }));
  const civilStatusData = Object.entries(kpis.civilStatus || {}).map(([name, value]) => ({ name, value }));
  const ageData = Object.entries(kpis.ageDistribution || {}).map(([name, value]) => ({ name, value }));
  const tenureData = Object.entries(kpis.tenureDistribution || {}).map(([name, value]) => ({ name, value }));
  const departmentData = Object.entries(kpis.departmentDistribution || {})
    .sort((a, b) => b[1] - a[1])
    .map(([name, value]) => ({ name, value }));
  const employmentStatusData = Object.entries(kpis.employmentStatusDistribution || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">KPI Dashboard</h1>
        <p className="text-gray-600 mt-1">Key performance indicators and organizational metrics</p>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="shadow-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-[#C9A64A]">{kpis.avgTenure}</div>
                <div className="text-sm text-gray-600 mt-1">Average Tenure (YRS)</div>
              </div>
              <Calendar className="h-12 w-12 text-[#C9A64A] opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-green-600">{kpis.femalePercentage}%</div>
                <div className="text-sm text-gray-600 mt-1">Female</div>
              </div>
              <Users className="h-12 w-12 text-green-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-purple-600">{kpis.avgAge}</div>
                <div className="text-sm text-gray-600 mt-1">Average Age</div>
              </div>
              <UserCheck className="h-12 w-12 text-purple-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold text-orange-600">{kpis.malePercentage}%</div>
                <div className="text-sm text-gray-600 mt-1">Male</div>
              </div>
              <Users className="h-12 w-12 text-orange-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Total Employees Card */}
      <Card className="shadow-md">
        <CardContent className="pt-6">
          <div className="text-center">
            <Users className="h-16 w-16 text-[#C9A64A] mx-auto mb-4" />
            <div className="text-5xl font-bold text-gray-900">{kpis.totalEmployees}</div>
            <div className="text-gray-600 mt-2">Total Employees</div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Employee Type */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle>Employee Type</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={employeeTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {employeeTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Civil Status */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle>Civil Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={civilStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {civilStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Age Distribution */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle>Age Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={ageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="name" stroke="#666" />
                <YAxis stroke="#666" />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e0e0e0' }} />
                <Bar dataKey="value" fill="#3b82f6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Tenure Distribution */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle>Tenure</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={tenureData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis type="number" stroke="#666" />
                <YAxis dataKey="name" type="category" width={100} stroke="#666" />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e0e0e0' }} />
                <Bar dataKey="value" fill="#f59e0b" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Department Distribution */}
        <Card className="shadow-md lg:col-span-2">
          <CardHeader>
            <CardTitle>Department Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={departmentData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="name" stroke="#666" />
                <YAxis stroke="#666" />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e0e0e0' }} />
                <Bar dataKey="value" fill="#10b981" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Employment Status */}
        <Card className="shadow-md lg:col-span-2">
          <CardHeader>
            <CardTitle>Employment Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={employmentStatusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="name" stroke="#666" />
                <YAxis stroke="#666" />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e0e0e0' }} />
                <Bar dataKey="value" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KPIDashboard;
